import { parseISO } from 'date-fns';

import { minutesToHours } from '@/helpers/date-format';
import {
  DEFAULT_PAGE_SIZE_APPROVALS,
  DEFAULT_PAGE_SIZE_TIME_TRACKS,
} from '@/lib/constants/pagination';
import { ApiErrorCode, ApiResponse, withApiErrorHandler } from '@/modules/common/api/errors';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { mapTimeOffRequestFromDTO } from '@/modules/time-off/mappers/time-off-request.mapper';
import { axiosInstance } from '@/utils/axios';

import { calculateProgressPercentage } from '../helpers/progress-percentage.helper';
import {
  mapDayOverviewFromResponse,
  mapMonthOverviewFromResponse,
  mapWeekOverviewFromResponse,
} from '../mappers/overview.mapper';
import { mapTimeOffRequestSummaryStatusFromDTO } from '../mappers/time-off-summary.mapper';
import { mapTimeTracksFromResponse } from '../mappers/worklog.mapper';
import {
  ApprovalMemberContext,
  ApprovalProjectContext,
  mapApprovalProjectItemToRow,
  mapApprovalSelectionToRequest,
  mapApprovalSummaryFromResponse,
  mapApprovalTeamMemberToRow,
  mapApprovalWorklogToEntryRow,
} from '../mappers/worklogs-approval.mapper';
import {
  ApprovalProjectItemResponse,
  ApprovalProjectsResult,
  ApprovalSummaryItemResponse,
  ApprovalTeamMembersResponse,
  ApprovalWorklogsResponse,
  GetApprovalFilterRequest,
} from '../types/dto/worklogs-approval.dto';
import {
  CreateWorklogRequest,
  MonthViewRequest,
  MonthViewResponse,
  UpdateWorklogRequest,
  WeekViewRequest,
  WeekViewResponse,
  WorklogResponse,
  WorklogsSearchResponse,
  WorklogsSummaryResponse,
} from '../types/dto/worklogs-summary.dto';
import {
  DayOverview,
  MonthOverview,
  TimeTrack,
  WeekDayBreakdown,
  WeeklySummary,
} from '../types/time-tracking';
import {
  ApprovalConfirmMode,
  ApprovalEntryRow,
  ApprovalMemberRow,
  ApprovalProjectRow,
  ApprovalSelection,
  ApprovalsSummary,
} from '../types/time-tracking-approvals';
import {
  getMockDayOverview,
  getMockMonthOverview,
  getMockTimeTracks,
  getMockWeekOverview,
  getMockWorklogsSummary,
  isTimeTrackingMockEnabled,
} from './mocks/time-tracking.mock';

interface GetTimeTracksParams {
  startDate: string;
  endDate: string;
  projectId: number;
  cursor?: number;
}

export const getTimeTracks = async ({
  startDate,
  endDate,
  projectId,
  cursor,
}: GetTimeTracksParams): Promise<PaginatedResponse<TimeTrack>> => {
  if (isTimeTrackingMockEnabled()) {
    return getMockTimeTracks(startDate, endDate, projectId);
  }

  const endpoint = '/api/worklogs/search';

  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.WEEKLY_SUMMARY_LOAD_FAILED,
    () =>
      axiosInstance.post<{
        data: WorklogsSearchResponse;
        error?: { status: number; message: string; code: string };
      }>(endpoint, {
        filter: { startDate, endDate, projectId },
        pagination: { pageSize: DEFAULT_PAGE_SIZE_TIME_TRACKS, cursor },
      })
  );

  return new PaginatedResponse({
    items: mapTimeTracksFromResponse(response.items || []),
    pagination: {
      total: response.pagination?.total ?? 0,
      cursor: (response.pagination?.cursor as unknown as number | null) ?? null,
    },
  });
};

/**
 * Fetches worklogs summary for a given date range.
 * Converts minutes from API response to hours for UI display.
 */
export const getWorklogsSummary = async (
  startDate: string,
  endDate: string
): Promise<WeeklySummary> => {
  if (isTimeTrackingMockEnabled()) {
    return getMockWorklogsSummary(startDate, endDate);
  }

  const endpoint = '/api/worklogs/summary';

  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.WEEKLY_SUMMARY_LOAD_FAILED,
    () =>
      axiosInstance.post<{
        data: WorklogsSummaryResponse;
        error?: { status: number; message: string; code: string };
      }>(endpoint, { startDate, endDate })
  );

  const totalTrackedHours = minutesToHours(response.totalTrackedMinutes);
  const totalBookedHours = minutesToHours(response.effectiveBookedMinutes);
  const totalCapacityHours = minutesToHours(response.totalCapacityMinutes);
  const effectiveCapacityHours = minutesToHours(response.effectiveCapacityMinutes);

  const timeOffSummary = response.timeOffSummary.map(item => ({
    status: mapTimeOffRequestSummaryStatusFromDTO(item.status),
    totalHours: minutesToHours(item.totalMinutes),
  }));

  return {
    weekStartDate: startDate,
    weekEndDate: endDate,
    totalTrackedHours,
    trackedBreakdown: response.tracked.map(item => ({
      trackedHours: minutesToHours(item.minutes),
      isBackgroundWork: item.isBackgroundWork,
    })),
    totalBookedHours,
    totalCapacityHours,
    effectiveCapacityHours,
    timeOffSummary,
    timeOffRequests: response.timeOffRequests.map(mapTimeOffRequestFromDTO),
    progressPercentage: calculateProgressPercentage(totalTrackedHours, totalBookedHours),
    projectSummaries: response.projectSummaries.map(ps => {
      const trackedHours = minutesToHours(ps.totalTrackedMinutes);
      const bookedHours = minutesToHours(ps.effectiveBookedMinutes);

      return {
        project: ps.project,
        trackedHours,
        trackedBreakdown: ps.tracked.map(item => ({
          trackedHours: minutesToHours(item.minutes),
          isBackgroundWork: item.isBackgroundWork,
        })),
        bookedHours,
        progressPercentage: calculateProgressPercentage(trackedHours, bookedHours),
        bookedBreakdown: ps.bookedBreakdown.map(item => ({
          bookedHours: minutesToHours(item.minutes),
          isBackgroundWork: item.isBackgroundWork,
        })),
      };
    }),
    bookedBreakdown: response.bookedBreakdown.map(item => ({
      bookedHours: minutesToHours(item.minutes),
      isBackgroundWork: item.isBackgroundWork,
    })),
  };
};

/**
 * Fetches week overview (per-day breakdown + per-project totals + time-off) for the week view.
 *
 * Backend endpoint pending — see OPS-1385. When `VITE_TIME_TRACKING_MOCK=true`,
 * returns deterministic mock data instead.
 */
export const getWeekOverview = async (
  startDate: string,
  endDate: string
): Promise<{ summary: WeeklySummary; dailyBreakdown: WeekDayBreakdown[] }> => {
  if (isTimeTrackingMockEnabled()) {
    return getMockWeekOverview(startDate, endDate);
  }

  const endpoint = '/api/worklogs/week-view';
  const body: WeekViewRequest = { startDate, endDate };

  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.WEEKLY_SUMMARY_LOAD_FAILED,
    () =>
      axiosInstance.post<{
        data: WeekViewResponse;
        error?: { status: number; message: string; code: string };
      }>(endpoint, body)
  );

  return mapWeekOverviewFromResponse(response);
};

/**
 * Fetches month overview (calendar days + per-project monthly totals).
 * Backend endpoint pending — see OPS-1385.
 */
export const getMonthOverview = async (
  startDate: string,
  endDate: string
): Promise<MonthOverview> => {
  if (isTimeTrackingMockEnabled()) {
    return getMockMonthOverview(startDate, endDate);
  }

  const endpoint = '/api/worklogs/month-view';
  const body: MonthViewRequest = { startDate, endDate };

  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.WEEKLY_SUMMARY_LOAD_FAILED,
    () =>
      axiosInstance.post<{
        data: MonthViewResponse;
        error?: { status: number; message: string; code: string };
      }>(endpoint, body)
  );

  return mapMonthOverviewFromResponse(response);
};

/**
 * Fetches detailed summary for a single day. Drives the month-view side panel.
 * Uses POST /api/worklogs/summary with a 1-day range (startDate = date, endDate = date + 1 day).
 */
export const getDayOverview = async (date: string): Promise<DayOverview> => {
  if (isTimeTrackingMockEnabled()) {
    return getMockDayOverview(date);
  }

  const endpoint = '/api/worklogs/summary';
  const parsed = parseISO(date);
  const endDate = new Date(
    Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate(), 23, 59, 59, 999)
  ).toISOString();

  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.WEEKLY_SUMMARY_LOAD_FAILED,
    () =>
      axiosInstance.post<{
        data: WorklogsSummaryResponse;
        error?: { status: number; message: string; code: string };
      }>(endpoint, { startDate: date, endDate })
  );

  return mapDayOverviewFromResponse(response, date);
};

export const createWorklog = async (values: CreateWorklogRequest): Promise<WorklogResponse> => {
  const endpoint = '/api/worklogs';

  return withApiErrorHandler(endpoint, ApiErrorCode.TIME_TRACK_ADD_FAILED, () =>
    axiosInstance.post<{
      data: WorklogResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint, values)
  );
};

export const updateWorklog = async (values: UpdateWorklogRequest): Promise<WorklogResponse> => {
  const endpoint = '/api/worklogs';

  return withApiErrorHandler(endpoint, ApiErrorCode.TIME_TRACK_UPDATE_FAILED, () =>
    axiosInstance.patch<{
      data: WorklogResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint, values)
  );
};

export const deleteWorklog = async (id: number): Promise<void> => {
  const endpoint = '/api/worklogs';

  await withApiErrorHandler(endpoint, ApiErrorCode.TIME_TRACK_DELETE_FAILED, () =>
    axiosInstance.delete<{
      data: void;
      error?: { status: number; message: string; code: string };
    }>(endpoint, { data: { id } })
  );
};

interface ApproveWorklogsParams {
  selection: ApprovalSelection;
  filter: GetApprovalFilterRequest;
}

export const approveWorklogs = async ({
  selection,
  filter,
}: ApproveWorklogsParams): Promise<void> => {
  const endpoint = '/api/worklogs/approval/approve';
  const selectionBody = mapApprovalSelectionToRequest(selection, ApprovalConfirmMode.Approve);

  await withApiErrorHandler(endpoint, ApiErrorCode.APPROVALS_APPROVE_FAILED, () =>
    axiosInstance.post<{ data: void }>(endpoint, { filter, ...selectionBody })
  );
};

interface UnapproveWorklogsParams {
  selection: ApprovalSelection;
  filter: GetApprovalFilterRequest;
}

export const unapproveWorklogs = async ({
  selection,
  filter,
}: UnapproveWorklogsParams): Promise<void> => {
  const endpoint = '/api/worklogs/approval/unapprove';
  const selectionBody = mapApprovalSelectionToRequest(selection, ApprovalConfirmMode.Unapprove);

  await withApiErrorHandler(endpoint, ApiErrorCode.APPROVALS_UNAPPROVE_FAILED, () =>
    axiosInstance.post<{ data: void }>(endpoint, { filter, ...selectionBody })
  );
};

const fetchApprovalProjects = async (
  filter: GetApprovalFilterRequest,
  cursor?: number | null
): Promise<ApprovalProjectsResult> => {
  const endpoint = '/api/worklogs/approval/projects';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.APPROVALS_LOAD_FAILED, () =>
    axiosInstance.post<{
      data: {
        items: ApprovalProjectItemResponse[];
        pagination: { total: number; totalWorklogs: number; cursor?: number | null };
      };
    }>(endpoint, {
      filter,
      pagination: { pageSize: DEFAULT_PAGE_SIZE_APPROVALS, cursor },
    })
  );

  return {
    items: response.items,
    totalWorklogs: response.pagination?.totalWorklogs ?? 0,
    pagination: {
      total: response.pagination?.total ?? response.items.length,
      cursor: response.pagination?.cursor ?? null,
    },
  };
};

const fetchApprovalTeamMembers = async (
  filter: GetApprovalFilterRequest,
  projectId: number,
  cursor?: number | null
): Promise<ApprovalTeamMembersResponse> => {
  const endpoint = '/api/worklogs/approval/team-members';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.APPROVALS_LOAD_FAILED, () =>
    axiosInstance.post<ApiResponse<ApprovalTeamMembersResponse>>(endpoint, {
      filter,
      projectId,
      pagination: { pageSize: DEFAULT_PAGE_SIZE_APPROVALS, cursor },
    })
  );

  return { items: response.items ?? [], pagination: response.pagination };
};

const fetchApprovalWorklogs = async (
  filter: GetApprovalFilterRequest,
  projectId: number,
  userId: number,
  cursor?: number | null
): Promise<ApprovalWorklogsResponse> => {
  const endpoint = '/api/worklogs/approval/worklogs';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.APPROVALS_LOAD_FAILED, () =>
    axiosInstance.post<ApiResponse<ApprovalWorklogsResponse>>(endpoint, {
      filter,
      projectId,
      userId,
      pagination: { pageSize: DEFAULT_PAGE_SIZE_APPROVALS, cursor },
    })
  );

  return { items: response.items ?? [], pagination: response.pagination };
};

/**
 * Fetches team members for a project on a specific date (used by the edit modal member dropdown).
 */
export const getApprovalTeamMembers = async (
  projectId: number,
  date: string
): Promise<{ id: number; name: string }[]> => {
  const endDate = new Date(date);
  endDate.setUTCHours(23, 59, 59, 999);

  const { items } = await fetchApprovalTeamMembers(
    { startDate: date, endDate: endDate.toISOString() },
    projectId
  );

  return items.map(m => ({ id: m.id, name: m.name }));
};

/**
 * A page of approval projects plus the period-wide worklog count. Extends
 * `PaginatedResponse` so the generic `getNextPageParam` drives cursor pagination,
 * while `totalWorklogs` (an aggregate, identical across pages) rides along for the
 * count display. Defensive about missing pagination metadata (see `getApprovalMembers`).
 */
export interface ApprovalProjectsPage extends PaginatedResponse<ApprovalProjectRow> {
  totalWorklogs: number;
}

export const getApprovalProjects = async (
  filter: GetApprovalFilterRequest,
  cursor?: number | null
): Promise<ApprovalProjectsPage> => {
  const { items, totalWorklogs, pagination } = await fetchApprovalProjects(filter, cursor);

  const page = new PaginatedResponse({
    items: items.map(mapApprovalProjectItemToRow),
    pagination: {
      total: pagination?.total ?? items.length,
      cursor: pagination?.cursor ?? null,
    },
  });

  return Object.assign(page, { totalWorklogs });
};

export const getApprovalSummary = async (
  filter: GetApprovalFilterRequest
): Promise<ApprovalsSummary> => {
  const endpoint = '/api/worklogs/approval/summary';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.APPROVALS_LOAD_FAILED, () =>
    axiosInstance.post<{ data: ApprovalSummaryItemResponse }>(endpoint, {
      startDate: filter.startDate,
      endDate: filter.endDate,
      ...(filter.projectIds?.length && { projectIds: filter.projectIds }),
      ...(filter.userIds?.length && { userIds: filter.userIds }),
      ...(filter.departmentIds?.length && { departmentIds: filter.departmentIds }),
      ...(filter.statuses?.length && { statuses: filter.statuses }),
      ...(filter.search && { search: filter.search }),
      ...(filter.customAttributeFilters?.length && {
        customAttributeFilters: filter.customAttributeFilters,
      }),
    })
  );

  return mapApprovalSummaryFromResponse(response);
};

/**
 * Fetches a page of approval team members for a project and maps to domain rows with
 * empty timeEntries (lazy-loaded on row expand). Cursor-paginated for infinite scroll.
 *
 * Defensive: when the backend omits pagination metadata, `total` falls back to the
 * page length and `cursor` to null, so `getNextPageParam` resolves to "no more pages"
 * (never an infinite loop).
 */
export const getApprovalMembers = async (
  filter: GetApprovalFilterRequest,
  projectId: number,
  cursor?: number | null
): Promise<PaginatedResponse<ApprovalMemberRow>> => {
  const { items, pagination } = await fetchApprovalTeamMembers(filter, projectId, cursor);

  return new PaginatedResponse({
    items: items.map(member => mapApprovalTeamMemberToRow(member, projectId)),
    pagination: {
      total: pagination?.total ?? items.length,
      cursor: pagination?.cursor ?? null,
    },
  });
};

/**
 * Fetches a page of worklogs for a project+member and maps to domain time entries with
 * denormalized project/member context (lazy-loaded on row expand). Cursor-paginated.
 *
 * Defensive about missing pagination metadata (see `getApprovalMembers`).
 */
export const getApprovalWorklogEntries = async (
  filter: GetApprovalFilterRequest,
  projectContext: ApprovalProjectContext,
  memberContext: ApprovalMemberContext,
  cursor?: number | null
): Promise<PaginatedResponse<ApprovalEntryRow>> => {
  const { items, pagination } = await fetchApprovalWorklogs(
    filter,
    projectContext.projectId,
    memberContext.memberId,
    cursor
  );

  return new PaginatedResponse({
    items: items.map(wl => mapApprovalWorklogToEntryRow(wl, projectContext, memberContext)),
    pagination: {
      total: pagination?.total ?? items.length,
      cursor: pagination?.cursor ?? null,
    },
  });
};
